import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';

const TransactionRecorder = ({ onClose, onTransactionRecorded }) => {
    const { restaurant } = useSelector((state) => state.auth);
    const restaurantId = restaurant?._id;
    const [ingredients, setIngredients] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        ingredientId: '',
        transactionType: 'purchase',
        quantity: '',
        notes: ''
    });

    useEffect(() => {
        fetchIngredients();
    }, []);

    const fetchIngredients = async () => {
        try {
            const response = await axios.get(`${API_ENDPOINTS.INVENTORY_INGREDIENTS}?restaurantId=${restaurantId}`);
            setIngredients(response.data);
        } catch (error) {
            console.error('Error fetching ingredients:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const dataWithRestaurantId = { ...formData, restaurantId };
            await axios.post(API_ENDPOINTS.INVENTORY_TRANSACTIONS, dataWithRestaurantId);
            onTransactionRecorded?.();
            onClose();
        } catch (error) {
            console.error('Error recording transaction:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const transactionTypes = [
        { value: 'purchase', label: 'Purchase' },
        { value: 'wastage', label: 'Wastage' },
        { value: 'adjustment', label: 'Adjustment' }
    ];

    return (
        <div
            className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col border border-stone-200 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-stone-200 bg-stone-50/80 flex-shrink-0">
                    <h3 className="text-base font-bold text-stone-800">Record Transaction</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-200 hover:text-stone-800 transition-colors text-lg leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                    <div className="overflow-y-auto flex-1 p-3 sm:p-4 space-y-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Ingredient</p>
                            <select
                                value={formData.ingredientId}
                                onChange={(e) => setFormData({ ...formData, ingredientId: e.target.value })}
                                className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-stone-800"
                                required
                            >
                                <option value="">Select ingredient</option>
                                {ingredients.map((ingredient) => (
                                    <option key={ingredient._id} value={ingredient._id}>
                                        {ingredient.name} — {ingredient.currentStock} {ingredient.unit}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Type</p>
                            <select
                                value={formData.transactionType}
                                onChange={(e) => setFormData({ ...formData, transactionType: e.target.value })}
                                className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-stone-800"
                            >
                                {transactionTypes.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Quantity</p>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.quantity}
                                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-stone-800"
                                placeholder="0"
                                required
                            />
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Notes (optional)</p>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-stone-800 placeholder:text-stone-400 resize-none"
                                rows={2}
                                placeholder="Add notes..."
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 px-3 sm:px-4 py-2.5 border-t border-stone-200 bg-white flex-shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                        >
                            {isSubmitting && (
                                <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            {isSubmitting ? 'Recording...' : 'Record'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TransactionRecorder;
